/**
 * Bundle card: resolves Shopify bundle variants from two (or more) option rows.
 */
class BundleCard extends HTMLElement {
  /** @type {Array<Record<string, any>>} */
  #variants = [];

  /** @type {string[]} */
  #selectedOptions = [];

  connectedCallback() {
    const jsonEl = this.querySelector('[data-bundle-variants]');
    if (!(jsonEl instanceof HTMLScriptElement)) return;

    try {
      this.#variants = JSON.parse(jsonEl.textContent || '[]');
    } catch (error) {
      console.warn('[bundle-card] Invalid variants JSON', error);
      return;
    }

    const optionGroups = this.querySelectorAll('[data-option-index]');
    this.#selectedOptions = Array.from(optionGroups).map((group) => {
      const selected = group.querySelector('.bundle-card__size.is-selected');
      return selected instanceof HTMLElement ? selected.dataset.optionValue || '' : '';
    });

    this.addEventListener('click', this.#onClick);
    this.#syncVariant();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const sizeButton = target.closest('.bundle-card__size[data-option-value]');
    if (!(sizeButton instanceof HTMLElement) || sizeButton.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    const optionGroup = sizeButton.closest('[data-option-index]');
    if (!(optionGroup instanceof HTMLElement)) return;

    const index = Number(optionGroup.dataset.optionIndex || '0');
    const value = sizeButton.dataset.optionValue || '';

    optionGroup.querySelectorAll('.bundle-card__size').forEach((button) => {
      const selected = button === sizeButton;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    this.#selectedOptions[index] = value;
    this.#syncVariant();
  };

  #syncVariant() {
    if (!this.#selectedOptions.length || this.#selectedOptions.some((value) => !value)) {
      this.#setUnavailable();
      return;
    }

    const match = this.#variants.find((variant) => {
      const options = variant.options || [];
      if (options.length !== this.#selectedOptions.length) return false;
      return options.every((option, index) => String(option) === String(this.#selectedOptions[index]));
    });

    if (!match) {
      this.#setUnavailable();
      return;
    }

    const variantInput = this.querySelector('[data-bundle-variant]');
    const addButton = this.querySelector('[data-bundle-add]');
    const priceEl = this.querySelector('[data-bundle-price]');
    const compareEl = this.querySelector('[data-bundle-compare]');

    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = String(match.id);
      variantInput.disabled = !match.available;
    }

    if (addButton instanceof HTMLButtonElement) {
      addButton.disabled = !match.available;
    }

    if (priceEl instanceof HTMLElement && match.price) {
      priceEl.textContent = match.price;
    }

    if (compareEl instanceof HTMLElement) {
      if (match.compareAtPrice) {
        compareEl.textContent = match.compareAtPrice;
        compareEl.hidden = false;
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
    }
  }

  #setUnavailable() {
    const variantInput = this.querySelector('[data-bundle-variant]');
    const addButton = this.querySelector('[data-bundle-add]');
    if (variantInput instanceof HTMLInputElement) variantInput.disabled = true;
    if (addButton instanceof HTMLButtonElement) addButton.disabled = true;
  }
}

if (!customElements.get('bundle-card')) {
  customElements.define('bundle-card', BundleCard);
}
